import { useMemo } from "react";

import { useTypedSelector } from "../../redux/store";
import { hasAccess, AccessKey } from "../../access";

/**
 * Custom React hook to evaluate access control flags based on a provided access map.
 *
 * @template K - Generic type representing keys in the access map.
 * @param {Record<K, string>} accessMap - A mapping of logical keys to access permission names.
 * @returns {Record<K, boolean>} - A mapping of the same keys to booleans indicating access rights.
 */
const useCheckFlagAccessControl = <K extends string>(
	accessMap: Record<K, string>,
): Record<K, boolean> => {
	const personalRole = useTypedSelector(
		(state) => state.account.personalRole,
	);

	return useMemo(
		() =>
			Object.fromEntries(
				(Object.entries(accessMap) as [K, string][]).map(
					([key, accessName]) => [
						key,
						hasAccess(accessName, personalRole),
					],
				),
			) as Record<K, boolean>,
		[accessMap, personalRole],
	);
};

declare namespace useCheckFlagAccessControl {
	type AccessNameMap<K extends AccessKey> = Record<K, string>;
	type AccessBooleanMap<K extends AccessKey> = Record<K, boolean>;
}

export default useCheckFlagAccessControl;
